
import React, { useState, useEffect, useMemo } from 'react';
import { adminTradingService, AdminAggregatedTrade, AdminTradeLedgerEntry, AdminTradeDetail } from '../../services/admin.trading.service';
import { adminUsersService } from '../../services/admin.users.service';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import Select from '../../components/common/Select';
import MultiSelect from '../../components/common/MultiSelect';
import {
  Zap,
  Search,
  ChevronDown,
  ChevronUp,
  Plus,
  X,
  User,
  TrendingUp,
  TrendingDown,
  Activity,
  Calendar,
  DollarSign,
  ArrowRightLeft,
  Briefcase,
  Layers,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Eye,
  ShieldCheck,
  History,
  FileText,
  Info,
  Trash2
} from 'lucide-react';

const AdminTrading: React.FC = () => {
  const [tradingUsers, setTradingUsers] = useState<AdminAggregatedTrade[]>([]);
  const [customers, setCustomers] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedUsers, setExpandedUsers] = useState<Set<string>>(new Set());

  // Modals & Sidepanels
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedTradeId, setSelectedTradeId] = useState<string | null>(null);
  const [tradeToDelete, setTradeToDelete] = useState<string | null>(null);
  const [tradeDetail, setTradeDetail] = useState<AdminTradeDetail | null>(null);

  // Loading & Feedback
  const [isLoading, setIsLoading] = useState(false);
  const [isDetailLoading, setIsDetailLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toast, setToast] = useState<{ message: string, type: 'success' | 'error' } | null>(null);

  // New Trade Form State
  const [newTrade, setNewTrade] = useState({
    userIds: [] as string[],
    asset: 'BTC',
    customAsset: '',
    principal: '',
    outcome: 'PROFIT' as 'PROFIT' | 'LOSS' | 'DEPOSIT',
    settlementAmount: ''
  });

  const formatCurrency = (value: string | number) => {
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return isNaN(num) ? '0.00' : num.toFixed(2);
  };

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [tradingResponse, customersResponse] = await Promise.all([
        adminTradingService.getTradingLedger(),
        adminUsersService.listUsers({ limit: 100 })
      ]);

      const ledgerData = Array.isArray(tradingResponse)
        ? tradingResponse
        : (tradingResponse as any)?.data || [];

      setTradingUsers(Array.isArray(ledgerData) ? ledgerData : []);

      if (customersResponse.success && customersResponse.data) {
        const filtered = customersResponse.data.filter((u: any) => u.walletId);
        setCustomers(filtered);
      }
    } catch (error) {
      console.error("Failed to fetch trading data:", error);
      setToast({ message: "Sync Failure: Market Engine Disconnected", type: 'error' });
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchTradeDetail = async (id: string) => {
    setIsDetailLoading(true);
    try {
      const response = await adminTradingService.getTradeDetails(id);
      if (response.success) {
        setTradeDetail(response.data);
      } else {
        setToast({ message: "Failed to resolve trade audit data", type: 'error' });
      }
    } catch (error) {
      setToast({ message: "Trade vault access error", type: 'error' });
    } finally {
      setIsDetailLoading(false);
    }
  };

  useEffect(() => {
    if (selectedTradeId) {
      fetchTradeDetail(selectedTradeId);
    } else {
      setTradeDetail(null);
    }
  }, [selectedTradeId]);

  const filteredUsers = useMemo(() => {
    return (tradingUsers || []).filter(u =>
      (u.userName || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (u.userId || '').toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [tradingUsers, searchQuery]);

  const toggleUser = (userId: string) => {
    const next = new Set(expandedUsers);
    if (next.has(userId)) next.delete(userId);
    else next.add(userId);
    setExpandedUsers(next);
  };

  const handleAddTrade = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newTrade.userIds.length === 0 || !newTrade.principal) return;

    setIsSubmitting(true);
    try {
      const finalAsset = newTrade.asset === 'OTHERS' ? newTrade.customAsset : newTrade.asset;
      const response = await adminTradingService.executeTrade({
        userIds: newTrade.userIds,
        asset: finalAsset || 'UNKNOWN',
        principal: newTrade.principal,
        outcome: newTrade.outcome,
        settlementAmount: (newTrade.outcome === 'PROFIT' || newTrade.outcome === 'DEPOSIT') ? newTrade.settlementAmount : '0'
      });

      if (response.success) {
        setToast({
          message: response.message || `${newTrade.userIds.length} Trade(s) Committed to Ledger Successfully`,
          type: 'success'
        });
        setShowAddModal(false);
        setNewTrade({ userIds: [], asset: 'BTC', customAsset: '', principal: '', outcome: 'PROFIT', settlementAmount: '' });
        fetchData();
      } else {
        setToast({ message: 'Execution Rejected by Core Engine', type: 'error' });
      }
    } catch (error) {
      setToast({ message: 'Protocol Timeout: Execution Failed', type: 'error' });
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setToast(null), 3000);
    }
  };

  const handleDeleteTrade = async () => {
    if (!tradeToDelete) return;
    setIsSubmitting(true);
    try {
      const response = await adminTradingService.deleteTrade(tradeToDelete);
      if (response.success) {
        setToast({ message: 'Trade Purged from Ledger', type: 'success' });
        setTradeToDelete(null);
        fetchData();
      } else {
        setToast({ message: 'Deletion Failed: Core Logic Rejection', type: 'error' });
      }
    } catch (error) {
      setToast({ message: 'Protocol Error during Purge', type: 'error' });
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setToast(null), 3000);
    }
  };

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-700 pb-24">

      {/* 1. Dashboard Header */}
      <section className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div>
          <h1 className="text-3xl font-bold text-white tracking-tight uppercase italic text-brand-primary">Market Engine Control</h1>
          <p className="text-brand-muted text-xs font-bold uppercase tracking-widest opacity-70">Customer-wise Portfolio Audit & Execution</p>
        </div>

        <div className="flex items-center gap-3">
          <div className="hidden md:block relative w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted" size={14} />
            <input
              type="text"
              placeholder="Search Entities..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-brand-card border border-brand-border rounded-xl pl-10 pr-4 py-2 text-xs text-white focus:outline-none focus:border-brand-primary transition-all"
            />
          </div>
          <Button onClick={() => setShowAddModal(true)} className="h-12 px-6 shadow-xl shadow-brand-primary/10">
            <Plus size={18} />
            EXECUTE TRADE
          </Button>
        </div>
      </section>

      {/* 2. Feedback Toasts */}
      {toast && (
        <div className={`fixed top-8 right-8 z-[250] px-6 py-4 rounded-2xl flex items-center gap-3 animate-in slide-in-from-right-10 duration-500 shadow-2xl border ${toast.type === 'success' ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-500' : 'bg-red-500/10 border-red-500/20 text-red-500'}`}>
          {toast.type === 'success' ? <CheckCircle2 size={18} /> : <AlertCircle size={18} />}
          <span className="text-xs font-black uppercase tracking-widest">{toast.message}</span>
        </div>
      )}

      {/* 3. Customer Ledger Grid */}
      <div className="bg-brand-card border border-brand-border rounded-3xl overflow-hidden shadow-2xl relative min-h-[400px]">
        {isLoading && (
          <div className="absolute inset-0 bg-brand-dark/20 backdrop-blur-[2px] z-10 flex items-center justify-center">
            <Loader2 className="animate-spin text-brand-primary" size={32} />
          </div>
        )}

        <div className="hidden md:block">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-brand-dark/40 border-b border-brand-border">
                <th className="px-6 py-5 text-[10px] font-black text-brand-muted uppercase tracking-[0.2em] w-12"></th>
                <th className="px-6 py-5 text-[10px] font-black text-brand-muted uppercase tracking-[0.2em]">Customer Identity</th>
                <th className="px-6 py-5 text-[10px] font-black text-brand-muted uppercase tracking-[0.2em]">Executions</th>
                <th className="px-6 py-5 text-[10px] font-black text-brand-muted uppercase tracking-[0.2em]">Aggregated Principal</th>
                <th className="px-6 py-5 text-[10px] font-black text-brand-muted uppercase tracking-[0.2em]">Final Settlement</th>
                <th className="px-6 py-5 text-[10px] font-black text-brand-muted uppercase tracking-[0.2em] text-right">Net P/L Delta</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-brand-border">
              {filteredUsers.length === 0 && !isLoading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-20 text-center text-brand-muted text-xs font-bold uppercase tracking-widest opacity-50">
                    No matching market entities found
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user) => {
                  const isExpanded = expandedUsers.has(user.userId);
                  const netPnlVal = parseFloat(user.netPnl);
                  return (
                    <React.Fragment key={user.userId}>
                      <tr
                        onClick={() => toggleUser(user.userId)}
                        className={`hover:bg-brand-primary/5 transition-colors cursor-pointer group ${isExpanded ? 'bg-brand-primary/5' : ''}`}
                      >
                        <td className="px-6 py-5">
                          {isExpanded ? <ChevronUp size={16} className="text-brand-primary" /> : <ChevronDown size={16} className="text-brand-muted" />}
                        </td>
                        <td className="px-6 py-5">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-lg bg-brand-dark border border-brand-border flex items-center justify-center text-xs font-bold text-brand-muted">
                              {user.userName?.charAt(0) || 'U'}
                            </div>
                            <div>
                              <p className="text-sm font-bold text-white">{user.userName}</p>
                              <p className="text-[9px] text-brand-muted font-mono opacity-60 uppercase">{user.userId.slice(0, 13)}...</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-5">
                          <span className="text-[11px] font-mono text-white bg-brand-dark px-2 py-1 rounded border border-brand-border">{user.executionsCount} Trades</span>
                        </td>
                        <td className="px-6 py-5">
                          <span className="text-sm font-bold text-white font-mono">${formatCurrency(user.aggregatedPrincipal)}</span>
                        </td>
                        <td className="px-6 py-5">
                          <span className="text-sm font-bold text-white font-mono">${formatCurrency(user.finalSettlement)}</span>
                        </td>
                        <td className="px-6 py-5 text-right">
                          <span className={`text-sm font-black font-mono ${netPnlVal >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                            {netPnlVal >= 0 ? '+' : ''}${formatCurrency(user.netPnl)}
                          </span>
                        </td>
                      </tr>

                      {isExpanded && (
                        <tr className="bg-brand-dark/40 border-y border-brand-border/30">
                          <td colSpan={6} className="p-0">
                            <div className="px-16 py-8 animate-in slide-in-from-top-4 duration-300">
                              <h4 className="text-[10px] font-black text-brand-muted uppercase tracking-[0.3em] mb-4 flex items-center gap-2">
                                <Layers size={14} className="text-brand-primary" />
                                Detailed Execution Ledger
                              </h4>
                              <div className="grid grid-cols-7 gap-0 border border-brand-border rounded-2xl overflow-hidden shadow-lg bg-brand-card">
                                {['ID', 'ASSET', 'PRINCIPAL', 'OUTCOME', 'PAYOUT', 'TIMESTAMP', 'ACTION'].map(h => (
                                  <div key={h} className="p-3 bg-brand-dark text-[9px] font-black text-brand-muted uppercase tracking-widest border-b border-brand-border">{h}</div>
                                ))}
                                {user.trades.map(t => (
                                  <React.Fragment key={t.tradeId}>
                                    <div className="p-3 text-[11px] font-mono text-brand-muted border-b border-brand-border/30 truncate">{t.tradeId}</div>
                                    <div className="p-3 text-[11px] font-bold text-white border-b border-brand-border/30">{t.asset}</div>
                                    <div className="p-3 text-[11px] font-mono text-white border-b border-brand-border/30">${formatCurrency(t.principal)}</div>
                                    <div className="p-3 border-b border-brand-border/30">
                                      <span className={`text-[9px] font-black px-2 py-0.5 rounded uppercase ${t.outcome === 'PROFIT' ? 'bg-emerald-500/10 text-emerald-500' : 'bg-red-500/10 text-red-500'}`}>{t.outcome}</span>
                                    </div>
                                    <div className="p-3 text-[11px] font-mono text-white border-b border-brand-border/30">${formatCurrency(t.payout)}</div>
                                    <div className="p-3 text-[9px] text-brand-muted border-b border-brand-border/30 whitespace-nowrap">
                                      {new Date(t.executedAt).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })}
                                    </div>
                                    <div className="p-2 border-b border-brand-border/30 flex items-center gap-2">
                                      <button
                                        onClick={() => setSelectedTradeId(t.tradeId.toString())}
                                        className="p-1.5 rounded-lg bg-brand-dark border border-brand-border text-brand-muted hover:text-white hover:border-brand-primary/30 transition-all"
                                        title="View Audit Path"
                                      >
                                        <Eye size={14} />
                                      </button>
                                      <button
                                        onClick={() => setTradeToDelete(t.tradeId.toString())}
                                        className="p-1.5 rounded-lg bg-brand-dark border border-brand-border text-red-500/50 hover:text-red-500 hover:border-red-500/30 transition-all"
                                        title="Delete Trade Execution"
                                      >
                                        <Trash2 size={14} />
                                      </button>
                                    </div>
                                  </React.Fragment>
                                ))}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile View */}
        <div className="md:hidden divide-y divide-brand-border">
          {filteredUsers.map((user) => {
            const netPnlVal = parseFloat(user.netPnl);
            const isExpanded = expandedUsers.has(user.userId);
            return (
              <div key={user.userId} className={`p-5 space-y-4 transition-colors ${isExpanded ? 'bg-brand-primary/5' : ''}`}>
                <div className="flex justify-between items-start" onClick={() => toggleUser(user.userId)}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-brand-dark border border-brand-border flex items-center justify-center text-brand-primary font-bold">
                      {user.userName?.charAt(0) || 'U'}
                    </div>
                    <div>
                      <h4 className="font-bold text-white">{user.userName}</h4>
                      <p className="text-[9px] text-brand-muted uppercase font-black tracking-widest">{user.userId.slice(0, 13)}...</p>
                    </div>
                  </div>
                  {isExpanded ? <ChevronUp size={20} className="text-brand-primary" /> : <ChevronDown size={20} className="text-brand-muted" />}
                </div>

                <div className="grid grid-cols-2 gap-4 py-3 border-y border-brand-border/30">
                  <div className="space-y-1">
                    <p className="text-[9px] text-brand-muted font-black uppercase tracking-widest">Aggregated P/L</p>
                    <p className={`text-lg font-bold font-mono ${netPnlVal >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                      {netPnlVal >= 0 ? '+' : ''}${formatCurrency(user.netPnl)}
                    </p>
                  </div>
                  <div className="space-y-1 text-right">
                    <p className="text-[9px] text-brand-muted font-black uppercase tracking-widest">Trade Count</p>
                    <p className="text-lg font-bold text-white font-mono">{user.executionsCount}</p>
                  </div>
                </div>

                {isExpanded && (
                  <div className="space-y-4 pt-2 animate-in slide-in-from-top-2 duration-300">
                    {user.trades.map(t => (
                      <div key={t.tradeId} className="p-4 bg-brand-dark/50 border border-brand-border rounded-xl space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-[10px] font-mono text-brand-muted">{t.tradeId}</span>
                          <div className="flex items-center gap-2">
                            <span className={`text-[9px] font-black px-2 py-0.5 rounded uppercase ${t.outcome === 'PROFIT' ? 'bg-emerald-500/10 text-emerald-500' : 'bg-red-500/10 text-red-500'}`}>{t.outcome}</span>
                            <button
                              onClick={() => setSelectedTradeId(t.tradeId.toString())}
                              className="text-brand-primary p-1"
                            >
                              <Eye size={16} />
                            </button>
                            <button
                              onClick={() => setTradeToDelete(t.tradeId.toString())}
                              className="text-red-500/70 p-1"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-bold text-white">{t.asset}</span>
                          <span className="text-xs font-mono text-white">${formatCurrency(t.principal)} → ${formatCurrency(t.payout)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* 4. Execution Terminal (Add Modal) */}
      {showAddModal && (
        <div className="fixed inset-0 z-[150] flex items-center justify-center p-0 md:p-6 bg-brand-dark/95 backdrop-blur-md animate-in fade-in duration-300">
          <div className="bg-brand-card border-l border-brand-border w-full max-w-xl h-full md:h-auto md:rounded-3xl overflow-hidden shadow-[0_0_100px_rgba(0,0,0,0.8)] flex flex-col animate-in zoom-in-95 duration-300">

            <div className="p-6 border-b border-brand-border flex items-center justify-between bg-brand-dark/40 shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-brand-primary/10 border border-brand-primary/20 flex items-center justify-center text-brand-primary">
                  <Activity size={24} />
                </div>
                <div>
                  <h3 className="text-white font-bold uppercase tracking-tight">Trade Execution Terminal</h3>
                  <p className="text-[10px] text-brand-muted uppercase font-bold tracking-widest mt-0.5">Nexus Liquidity Node #8821</p>
                </div>
              </div>
              <button onClick={() => setShowAddModal(false)} className="p-2 rounded-xl bg-brand-dark hover:bg-brand-border text-brand-muted hover:text-white transition-all">
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleAddTrade} className="p-8 space-y-8 overflow-y-auto">
              <div className="space-y-6">
                <MultiSelect
                  label="Target Customer Identities"
                  placeholder="Search and select customers..."
                  selectedValues={newTrade.userIds}
                  onChange={(values) => setNewTrade({ ...newTrade, userIds: values })}
                  options={customers.map(u => ({
                    value: u.id,
                    label: `${u.name} (ID: ${u.id.slice(0, 8)}...)`
                  }))}
                />

                <div className="grid grid-cols-2 gap-6">
                  <Select
                    label="Trading Asset Protocol"
                    value={newTrade.asset}
                    onChange={(e) => setNewTrade({ ...newTrade, asset: e.target.value })}
                    options={[
                      { value: 'BTC', label: 'Bitcoin (BTC)' },
                      { value: 'ETH', label: 'Ethereum (ETH)' },
                      { value: 'DOGE', label: 'Dogecoin (DOGE)' },
                      { value: 'OTHERS', label: 'Other Asset...' }
                    ]}
                  />
                  <Select
                    label="Market Outcome"
                    value={newTrade.outcome}
                    onChange={(e) => setNewTrade({ ...newTrade, outcome: e.target.value as 'PROFIT' | 'LOSS' | 'DEPOSIT' })}
                    options={[
                      { value: 'PROFIT', label: 'PROFIT (WIN)' },
                      { value: 'LOSS', label: 'DEFICIT (LOSS)' },
                      { value: 'DEPOSIT', label: 'REVENUE (DEPOSIT)' }
                    ]}
                  />
                </div>

                {newTrade.asset === 'OTHERS' && (
                  <div className="animate-in slide-in-from-top-2 duration-300">
                    <Input
                      label="Custom Asset Name"
                      placeholder="e.g. SOL, XRP, USDT"
                      value={newTrade.customAsset}
                      onChange={(e) => setNewTrade({ ...newTrade, customAsset: e.target.value.toUpperCase() })}
                      required
                    />
                  </div>
                )}

                <div className="grid grid-cols-2 gap-6">
                  <Input
                    label="Principal Investment (USD)"
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={newTrade.principal}
                    onChange={(e) => setNewTrade({ ...newTrade, principal: e.target.value })}
                    required
                  />
                  <Input
                    label="Settlement Payout (USD)"
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={newTrade.settlementAmount}
                    onChange={(e) => setNewTrade({ ...newTrade, settlementAmount: e.target.value })}
                    disabled={newTrade.outcome === 'LOSS'}
                    required={newTrade.outcome === 'PROFIT' || newTrade.outcome === 'DEPOSIT'}
                  />
                </div>
              </div>

              <div className="pt-6 border-t border-brand-border/30">
                <Button type="submit" className="w-full h-14 text-sm font-black" isLoading={isSubmitting}>
                  <Zap size={18} />
                  COMMIT EXECUTION TO LEDGER
                </Button>
                <div className="mt-4 flex items-center justify-center gap-2">
                  <Briefcase size={12} className="text-brand-muted" />
                  <span className="text-[9px] text-brand-muted uppercase font-bold tracking-[0.2em]">All trades are immutable once committed.</span>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 5. Trade Detail Modal (Inspection) */}
      {selectedTradeId && (
        <div className="fixed inset-0 z-[160] flex items-center justify-center p-0 md:p-6 bg-brand-dark/95 backdrop-blur-xl animate-in fade-in duration-300">
          <div className="bg-brand-card border border-brand-border/50 w-full max-w-2xl md:rounded-3xl overflow-hidden shadow-[0_0_150px_rgba(0,0,0,1)] flex flex-col animate-in zoom-in-95 duration-300">

            <div className="p-6 border-b border-brand-border flex items-center justify-between bg-brand-dark/40">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-brand-primary/10 border border-brand-primary/20 flex items-center justify-center text-brand-primary">
                  <History size={24} />
                </div>
                <div>
                  <h3 className="text-white font-bold uppercase tracking-tight">Execution Audit Path</h3>
                  <p className="text-[10px] text-brand-muted uppercase font-bold tracking-widest mt-0.5">Protocol Hash: {selectedTradeId}</p>
                </div>
              </div>
              <button onClick={() => setSelectedTradeId(null)} className="p-2 rounded-xl bg-brand-dark hover:bg-brand-border text-brand-muted hover:text-white transition-all">
                <X size={20} />
              </button>
            </div>

            <div className="p-8 space-y-8 relative min-h-[300px]">
              {isDetailLoading ? (
                <div className="absolute inset-0 flex items-center justify-center bg-brand-card/50 backdrop-blur-sm z-20">
                  <Loader2 className="animate-spin text-brand-primary" size={40} />
                </div>
              ) : tradeDetail && (
                <div className="space-y-8 animate-in fade-in duration-500">
                  <div className="grid grid-cols-2 gap-6">
                    <div className="p-4 bg-brand-dark/50 border border-brand-border rounded-2xl space-y-1">
                      <p className="text-[9px] text-brand-muted font-black uppercase tracking-widest">Trading Asset</p>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-brand-primary animate-pulse" />
                        <p className="text-xl font-black text-white">{tradeDetail.asset}</p>
                      </div>
                    </div>
                    <div className="p-4 bg-brand-dark/50 border border-brand-border rounded-2xl space-y-1 text-right">
                      <p className="text-[9px] text-brand-muted font-black uppercase tracking-widest">Market Outcome</p>
                      <p className={`text-xl font-black ${tradeDetail.result.outcome === 'PROFIT' ? 'text-emerald-500' : 'text-red-500'}`}>
                        {tradeDetail.result.outcome}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h4 className="text-[10px] font-black text-brand-muted uppercase tracking-[0.2em] flex items-center gap-2">
                      <ShieldCheck size={14} className="text-brand-primary" />
                      Ledger Verification Metadata
                    </h4>
                    <div className="grid grid-cols-1 gap-2">
                      <DetailField icon={<DollarSign size={14} />} label="Principal Commitment" value={`$${formatCurrency(tradeDetail.principal)}`} />
                      <DetailField icon={<ArrowRightLeft size={14} />} label="Settlement Amount" value={`$${formatCurrency(tradeDetail.result.settlementAmount)}`} />
                      <DetailField icon={<User size={14} />} label="Target Entity UUID" value={tradeDetail.userId} mono />
                      <DetailField icon={<User size={14} />} label="Executing Administrator" value={tradeDetail.executedBy} mono />
                      <DetailField icon={<Calendar size={14} />} label="Execution Timestamp" value={new Date(tradeDetail.executedAt).toLocaleString()} />
                      <DetailField icon={<FileText size={14} />} label="System Audit Log" value={`ID: ${tradeDetail.id} | Legacy: ${tradeDetail.oldId}`} />
                    </div>
                  </div>

                  <div className="p-4 bg-brand-primary/5 border border-brand-primary/10 rounded-2xl flex items-center gap-4">
                    <Info size={18} className="text-brand-primary" />
                    <p className="text-[10px] text-brand-muted uppercase font-bold tracking-widest leading-relaxed">
                      This execution has been finalized and committed to the immutable core ledger. No further modifications are permitted.
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div className="p-6 bg-brand-dark/40 border-t border-brand-border flex justify-end">
              <Button onClick={() => setSelectedTradeId(null)} variant="secondary" className="px-8 font-black uppercase tracking-widest text-[10px]">
                Close Audit
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* 6. Delete Confirmation Modal */}
      {tradeToDelete && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center p-6 bg-brand-dark/95 backdrop-blur-md animate-in fade-in duration-300">
          <div className="bg-brand-card border border-brand-border/50 max-w-sm w-full rounded-3xl overflow-hidden shadow-2xl animate-in zoom-in-95 duration-300">
            <div className="p-8 text-center space-y-6">
              <div className="w-16 h-16 bg-red-500/10 border border-red-500/20 rounded-2xl flex items-center justify-center text-red-500 mx-auto">
                <Trash2 size={32} />
              </div>
              <div className="space-y-2">
                <h3 className="text-xl font-bold text-white uppercase italic">Delete Trade Execution?</h3>
                <p className="text-xs text-brand-muted leading-relaxed font-bold uppercase tracking-widest opacity-70">
                  This action will permanently delete the trade and its result. This cannot be undone.
                </p>
              </div>
              <div className="flex flex-col gap-3 pt-4">
                <Button
                  onClick={handleDeleteTrade}
                  isLoading={isSubmitting}
                  className="w-full h-12 bg-red-500 hover:bg-red-600 text-white border-none shadow-lg shadow-red-500/20 font-black"
                >
                  DELETE EXECUTION
                </Button>
                <Button
                  onClick={() => setTradeToDelete(null)}
                  variant="secondary"
                  className="w-full h-12 border-brand-border font-black text-brand-muted"
                >
                  CANCEL
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const DetailField: React.FC<{ icon: React.ReactNode, label: string, value: string, mono?: boolean }> = ({ icon, label, value, mono }) => (
  <div className="flex items-center gap-4 p-3 bg-brand-card/50 border border-brand-border/30 rounded-xl">
    <div className="text-brand-primary opacity-60">
      {icon}
    </div>
    <div className="flex-1">
      <div className="flex justify-between items-center">
        <p className="text-[9px] text-brand-muted uppercase font-black tracking-widest mb-0.5">{label}</p>
        <p className={`text-[11px] font-bold text-white ${mono ? 'font-mono' : ''}`}>{value}</p>
      </div>
    </div>
  </div>
);

export default AdminTrading;
